import { addFederationRoutes } from '@src/app/router';
import {
    ActionItem,
    AddMobileRouteActionItems,
    AddRoutes,
    AddTopLevelNavigation,
    ContextSwitcher,
    NavigationContext,
    NavigationContextProps,
    NavigationDestination,
    NavigationKind,
    RouteActionItems,
    Site,
    SocialRoutes,
    Country,
    ContextNavigationItem,
} from '@superbet-group/web.types';
import { Store } from 'vuex';
import { sportRoutes } from '@app/router/routes/sport';
import { RouteName } from '@src/app/router/constants/RouteName';
import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import { competitionRoutes } from '@src/app/router/routes/competition';
import { offerRedirectRoutes } from '@src/app/router/routes/offerRedirects';
import { getConfig } from '@superbet-group/web.lib.config';

import { isSocialEnabledOrAppEnabled } from '@socialFederation/helpers';
import { getShellConfig } from '@src/config/shell/config';

const areSitesSplitByLicence = getShellConfig().remoteConfig.areSitesSplitByLicence;

export function initializeSportsFederation(store: Store<unknown>) {
    initializeFederation({
        addRoutes: addFederationRoutes,
        addTopLevelNavigation: (key: NavigationContext, value: NavigationContextProps) => {
            store.dispatch('navigation/addTopLevelNavigation', { key, value });
        },
        addMobileRouteActionItems: (routeActionItems: RouteActionItems) => {
            store.dispatch('navigation/addMobileRouteActionItems', routeActionItems);
        },
        store,
    });
}

type SportsFederationOptions = {
    addRoutes: AddRoutes,
    addTopLevelNavigation: AddTopLevelNavigation,
    addMobileRouteActionItems: AddMobileRouteActionItems,
    store: Store<unknown>,
};

function initializeFederation(options: SportsFederationOptions) {
    addSportRoutes(options.addRoutes);
    addMobileRouteActionItems(options.addMobileRouteActionItems);
    addTopLevelNavigation(options.addTopLevelNavigation, options.store);
}

function addSportRoutes(addRoutes: AddRoutes) {
    addRoutes([
        ...sportRoutes,
        ...competitionRoutes,
        ...offerRedirectRoutes,
    ], undefined, NavigationContext.Sports);
}

function addMobileRouteActionItems(addMobileRouteActionItems: AddMobileRouteActionItems) {
    const searchIcon: ActionItem = {
        iconName: 'navigation-search',
        ariaLabel: 'search',
        onClick: (_route, router) => {
            router.push({
                name: RouteName.Sport.Search,
            });
        },
    };
    const mobileRouteActionItems: RouteActionItems = {
        [RouteName.Sport.CategoryPerTime]: [searchIcon],
        [RouteName.Sport.SportCompetitions]: [searchIcon],
        [RouteName.Sport.PrematchOffer]: [searchIcon],
        [RouteName.App.Home]: [searchIcon],
        // move to social fed once we extract offer routes to web.types
        [SocialRoutes.TicketFeed]: [searchIcon],
    };
    addMobileRouteActionItems(mobileRouteActionItems);
}

function addTopLevelNavigation(addTopLevelNavigation: AddTopLevelNavigation, store: Store<unknown>) {
    const props: NavigationContextProps = {
        contextSwitcher: getContextSwitcher(),
        headerBackgroundColor: 'var(--component-top-nav-bg-body)',
        desktopItems: [{
            label: 'navigation.sport',
            destination: {
                kind: NavigationKind.SingleDomain,
                location: {
                    name: RouteName.Sport.PrematchOffer,
                },
            },
            order: 1,
            isActive: () => store.getters['navigation/isOnSportsPage'],
            itemClasses: 'e2e-nav-sport',
        }, {
            label: 'bottomNavBar.live',
            destination: {
                kind: NavigationKind.SingleDomain,
                location: {
                    name: RouteName.Sport.CategoryPerTime,
                    params: {
                        timeFilter: TimeFilterSlug.Live,
                    },
                },
            },
            order: 2,
            badgeStoreGetter: 'data/sportOffer/liveEventsCount',
            shouldLoadBadge: true,
            isActive: () => store.getters['navigation/isOnSportLivePage'],
            itemClasses: 'e2e-nav-live',
        }],
        mobileItems: [
            {
                label: 'bottomNavBar.home',
                icon: 'navigation-home',
                destination: {
                    kind: NavigationKind.SingleDomain,
                    location: {
                        name: RouteName.App.Home,
                    },
                },
                isActive: (route) => store.getters['navigation/isOnHomePage'] || route.name === SocialRoutes.TicketFeed,
                order: 0,
                itemClasses: 'e2e-mobile-nav-sport-home',
            },
            {
                label: 'bottomNavBar.live',
                icon: 'time-live',
                destination: {
                    kind: NavigationKind.SingleDomain,
                    location: {
                        name: RouteName.Sport.CategoryPerTime,
                        params: {
                            timeFilter: TimeFilterSlug.Live,
                        },
                    },
                },
                badgeStoreGetter: 'data/sportOffer/liveEventsCount',
                isActive: () => store.getters['navigation/isOnSportLivePage'],
                order: 1,
                itemClasses: 'e2e-mobile-nav-sport-live',
            },
            {
                label: 'bottomNavBar.sport',
                icon: 'sports-soccer',
                destination: {
                    kind: NavigationKind.SingleDomain,
                    location: {
                        name: RouteName.Sport.PrematchOffer,
                    },
                },
                order: 2,
                isActive: () => store.getters['navigation/isOnSportsPage'],
                itemClasses: 'e2e-mobile-nav-sport-prematch',
            },
        ],
    };

    if (isSocialEnabledOrAppEnabled()) {
        const socialDesktopItem: ContextNavigationItem = {
            label: 'supersocial',
            destination: {
                kind: NavigationKind.SingleDomain,
                location: {
                    name: SocialRoutes.TicketFeed,
                },
            },
            order: 3,
            isActive: (route) => route.matched[0].name === SocialRoutes.Home,
            itemClasses: 'social-e2e-nav-supersocial',

        };

        props.desktopItems = [...props.desktopItems, socialDesktopItem];
    }

    addTopLevelNavigation(NavigationContext.Sports, props);
}

function getContextSwitcher(): ContextSwitcher {
    return {
        label: areSitesSplitByLicence ? 'navigation.napoleon-hub' : 'navigation.sport',
        icon: areSitesSplitByLicence ? 'navigation-explore' : 'sports-soccer',
        mobileIcon: areSitesSplitByLicence ? 'navigation-explore' : 'sports-soccer',
        destination: getContextSwitcherDestination(getConfig().countrySpecific.country),
        children: [],
    };
}

function getContextSwitcherDestination(country: Country): NavigationDestination {
    if (country === Country.Be) {
        return {
            kind: NavigationKind.MultipleDomain,
            location: {
                name: RouteName.App.Home,
            },
            site: Site.NapoleonGames,
            path: '/home',
        };
    } else {
        return {
            kind: NavigationKind.SingleDomain,
            location: {
                name: RouteName.App.Home,
            },
        };
    }
}
